import React from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, LogOut, Package, Users, CreditCard, Store, Receipt, BarChart2 } from 'lucide-react';

export function SettingsLayout() {
  const { role, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = () => { logout(); navigate('/login'); };

  const navItem = (to: string, label: string, icon: React.ReactNode) => {
    const active = isActive(to);
    return (
      <Link
        to={to}
        className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-[15px] font-semibold transition-all ${
          active
            ? 'bg-[#2563eb] text-white shadow-md'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-transparent'
        }`}
      >
        {icon}
        {label}
      </Link>
    );
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-[#f0f4f8] font-sans selection:bg-blue-200">
      {/* Sidebar */}
      <nav className="w-[280px] bg-white border-r border-slate-200/60 flex flex-col p-6 flex-shrink-0 relative z-20 shadow-2xl">
        {/* Brand */}
        <div className="flex items-center px-2 mb-4">
          <img src="/taplo-wordmark.png" alt="Taplo" className="h-7 select-none" draggable={false} />
        </div>

        {/* Back to POS */}
        <button
          onClick={() => navigate('/pos')}
          className="flex items-center gap-2.5 px-4 py-3 text-slate-500 font-semibold text-[15px] rounded-2xl hover:bg-slate-50 border border-transparent hover:border-slate-200 hover:text-slate-800 transition-all mb-2"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to POS
        </button>

        <div className="h-px bg-slate-100 my-4" />

        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest px-4 mb-4">
          Settings
        </div>

        <div className="flex flex-col gap-2 flex-1">
          {navItem('/settings/store', 'Store Settings', <Store className={`w-5 h-5 ${isActive('/settings/store') ? 'text-blue-200' : 'text-slate-400'}`} /> )}

          {navItem('/settings/products', 'Products', <Package className={`w-5 h-5 ${isActive('/settings/products') ? 'text-blue-200' : 'text-slate-400'}`} /> )}

          {navItem('/settings/transactions', 'Transactions', <Receipt className={`w-5 h-5 ${isActive('/settings/transactions') ? 'text-blue-200' : 'text-slate-400'}`} /> )}

          {navItem('/settings/reports', 'Reports', <BarChart2 className={`w-5 h-5 ${isActive('/settings/reports') ? 'text-blue-200' : 'text-slate-400'}`} /> )}

          {(role === 'owner') && navItem('/settings/users', 'Team & Users', <Users className={`w-5 h-5 ${isActive('/settings/users') ? 'text-blue-200' : 'text-slate-400'}`} /> )}

          {(role === 'owner') && navItem('/settings/stripe', 'Stripe Account', <CreditCard className={`w-5 h-5 ${isActive('/settings/stripe') ? 'text-blue-200' : 'text-slate-400'}`} /> )}
        </div>

        {/* Logout */}
        <div className="pt-4 border-t border-slate-100/80">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-500 font-semibold text-[15px] rounded-2xl hover:bg-red-50 transition-all text-left border border-transparent hover:border-red-100"
          >
            <LogOut className="w-5 h-5" />
            Log Out
          </button>
        </div>
      </nav>

      {/* Main content */}
      <main className="flex-1 overflow-auto h-full">
        <div className="px-6 md:px-10 py-2">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
